import { AxiosError } from 'axios';
import type { NavigateFunction } from 'react-router-dom';
import type { SchoolStudentFeatureKey } from '../modules/platform/types/featureAccess';
import { ROUTES } from './constants';
import { toastManager } from './notifications';

/** Backend error code for school-disabled student features (403). */
export const SCHOOL_FEATURE_DISABLED_CODE = 'FEATURE_NOT_ENABLED_FOR_SCHOOL';

const MESSAGES: Record<string, string> = {
  ai_tutor: 'Tamil Tutor is no longer available for your school.',
  communication_tutor: 'Communication Tutor is no longer available for your school.',
  scenario_based_convo: 'Scenario Conversation is no longer available for your school.',
  story_studio: 'Story Studio is no longer available for your school.',
  picture_talk: 'Picture Talk is no longer available for your school.',
};

const DEFAULT_MESSAGE = 'This feature is no longer available for your school.';

function readErrorCode(obj: Record<string, unknown>): string | undefined {
  const direct = obj.error_code;
  if (typeof direct === 'string') return direct;
  const err = obj.error;
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    if (typeof e.error_code === 'string') return e.error_code;
    if (typeof e.code === 'string') return e.code;
  }
  if (typeof obj.code === 'string') return obj.code;
  return undefined;
}

function readFeatureKey(obj: Record<string, unknown>): string | undefined {
  const details = obj.details;
  if (details && typeof details === 'object' && typeof (details as Record<string, unknown>).feature_key === 'string') {
    return (details as Record<string, unknown>).feature_key as string;
  }
  const err = obj.error;
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    const ed = e.details;
    if (ed && typeof ed === 'object' && typeof (ed as Record<string, unknown>).feature_key === 'string') {
      return (ed as Record<string, unknown>).feature_key as string;
    }
  }
  return undefined;
}

/** Returns parsed info only when status is 403 and body matches FEATURE_NOT_ENABLED_FOR_SCHOOL. */
export function parseSchoolFeatureDisabledFromBody(
  status: number,
  bodyText: string,
  parsedBody?: unknown
): { featureKey?: string } | null {
  if (status !== 403) return null;
  let obj: unknown = parsedBody;
  if (obj === undefined && bodyText.trim()) {
    try {
      obj = JSON.parse(bodyText) as unknown;
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== 'object') return null;
  const o = obj as Record<string, unknown>;
  const code = readErrorCode(o);
  if (code !== SCHOOL_FEATURE_DISABLED_CODE) return null;
  const fk = readFeatureKey(o);
  return { featureKey: fk };
}

export function getSchoolFeatureDisabledMessage(featureKey?: string | null): string {
  if (featureKey && MESSAGES[featureKey]) return MESSAGES[featureKey];
  return DEFAULT_MESSAGE;
}

/** Thrown after feature-disabled UX runs so callers skip generic error UI. */
export class SchoolFeatureDisabledHandledError extends Error {
  constructor() {
    super('SchoolFeatureDisabledHandled');
    this.name = 'SchoolFeatureDisabledHandledError';
  }
}

export function isSchoolFeatureDisabledHandledError(e: unknown): boolean {
  return e instanceof SchoolFeatureDisabledHandledError;
}

export async function handleSchoolFeatureDisabledIfPresent(options: {
  status: number;
  bodyText: string;
  parsedBody?: unknown;
  navigate: NavigateFunction;
  refetchFeatureAccess: () => Promise<void>;
  fallbackFeatureKey?: SchoolStudentFeatureKey;
}): Promise<boolean> {
  const parsed = parseSchoolFeatureDisabledFromBody(options.status, options.bodyText, options.parsedBody);
  if (!parsed) return false;

  const key = parsed.featureKey ?? options.fallbackFeatureKey;
  toastManager.info(getSchoolFeatureDisabledMessage(key ?? null));
  await options.refetchFeatureAccess();
  options.navigate(ROUTES.STUDENT_DASHBOARD, { replace: true });
  return true;
}

export async function handleSchoolFeatureDisabledFromAxiosError(
  error: unknown,
  navigate: NavigateFunction,
  refetchFeatureAccess: () => Promise<void>,
  fallbackFeatureKey?: SchoolStudentFeatureKey
): Promise<boolean> {
  if (!(error instanceof AxiosError)) return false;
  const status = error.response?.status;
  if (status !== 403) return false;
  const data = error.response?.data;
  const bodyText = typeof data === 'string' ? data : JSON.stringify(data ?? {});
  return handleSchoolFeatureDisabledIfPresent({
    status: 403,
    bodyText,
    parsedBody: typeof data === 'object' && data !== null ? data : undefined,
    navigate,
    refetchFeatureAccess,
    fallbackFeatureKey,
  });
}
